import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import {
	Container,
	Box,
	Stack,
	Tabs,
	Tab,
	Typography,
	IconButton,
	TextField
} from "@mui/material";
import { ConfigContext } from "../Config/ConfigContext";
import { useParams } from "react-router-dom";
import { debounce, kebabCase } from "lodash";
import { Add } from "@mui/icons-material";
import EnvironmentAddModal from "./environmentAddModal";
import Environment from "./environmentDetail";
import useApi from "../hooks/useApi";

/**
 * Fetches application and environment details.
 *
 * @param {Object} params - The parameters.
 * @param {Object} params.values - The values containing configuration and applicationId.
 * @param {Object} params.api - The API methods from useApi.
 * @returns {Promise<Object>} The loaded data with application and environments details.
 */
const onLoad = async ({ values, api }) => {
	const application = await api.application.getById({ id: values.applicationId });
	const environmentsResponse = await api.environment.search({ queryParams: { applicationId: values.applicationId } });
	return {
		...values,
		appId: application.appId,
		name: application.name,
		environments: environmentsResponse.rows
	};
};

/**
 * A functional component for Tab Panel.
 *
 * @param {Object} props - Component props.
 * @param {React.ReactNode} props.children - TabPanel children.
 * @param {number} props.value - Current tab value.
 * @param {number} props.index - Index of this TabPanel.
 * @returns {JSX.Element|null} The rendered TabPanel.
 */
function TabPanel(props) {
	const { children, value, index, ...other } = props;
	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			{...other}
		>
			{value === index && (
				<Box sx={{ p: 3 }}>
					<Typography>{children}</Typography>
				</Box>
			)}
		</div>
	);
}

TabPanel.propTypes = {
	children: PropTypes.node.isRequired,
	value: PropTypes.number.isRequired,
	index: PropTypes.number.isRequired
};

/**
 * ApplicationDetail component displays application settings and environments.
 *
 * @returns {JSX.Element} The ApplicationDetail component.
 */
export default function ApplicationDetail() {
	const { config } = useContext(ConfigContext);
	const [value, setValue] = useState(0);
	const { applicationId } = useParams();
	const api = useApi();

	/**
	 * Debounced save function using API's application.update.
	 *
	 * @param {Object} data - The data to save.
	 * @returns {Promise<void>}
	 */
	const saveField = debounce(async (data) => {
		await api.application.update({ id: applicationId, data: data });
	}, 500);

	/**
	 * Handles the tab change.
	 *
	 * @param {Object} event - The event object.
	 * @param {number} newValue - The new tab index.
	 */
	const handleChange = (_, newValue) => {
		setValue(newValue);
	};

	const [form, setForm] = useState({
		applicationId: applicationId,
		name: "",
		appId: "",
		application: {},
		environments: [],
		config,
		environmentAddModalIsOpen: false,
		environmentDeleteModalIsOpen: false,
		applicationURIAddModalIsOpen: false,
		applicationURIDeleteModalIsOpen: false,
		acceptedDomainAddModalIsOpen: false,
		acceptedDomainDeleteModalIsOpen: false
	});

	useEffect(() => {
		async function load() {
			const loaded = await onLoad({ values: form, api });
			setForm(loaded);
		}
		load();
	}, []);

	/**
	 * Handles field value change.
	 *
	 * @param {string} value - The new value.
	 * @param {string} field - The field to update.
	 */
	const onChange = (value, field) => {
		setForm((prev) => ({ ...prev, [field]: value }));
	};

	/**
	 * Handles bulk field changes.
	 *
	 * @param {Object} data - The data object containing new values.
	 */
	const onChangeBulk = (data) => {
		setForm((prev) => ({ ...prev, ...data }));
	};

	/**
	 * Callback for handling invalid form scenarios.
	 */
	const onInvalidForm = () => {};

	return (
		<Container className="bg-white">
			<EnvironmentAddModal
				onChange={onChange}
				onChangeBulk={onChangeBulk}
				onInvalidForm={onInvalidForm}
				config={config}
				isOpen={form.environmentAddModalIsOpen}
				applicationId={applicationId}
			/>

			<Typography variant="h5" component={"div"}>
				Application Settings
			</Typography>
			<Box>
				<Stack gap={2} direction={"row"} justifyContent={"left"} flexWrap={"wrap"}>
					<TextField
						label="Application Name"
						value={form.name}
						onChange={(e) => {
							const value = e.target.value;
							onChange(kebabCase(value), "appId");
							onChange(value, "name");
							saveField({ name: value });
						}}
						fullWidth
					/>
					<TextField
						label="App Id"
						value={form.appId}
						onChange={(e) => {
							const value = e.target.value;
							onChange(value, "appId");
							saveField({ appId: value });
						}}
						fullWidth
					/>
				</Stack>
			</Box>

			<Tabs
				value={value}
				onChange={handleChange}
				variant="scrollable"
				scrollButtons="auto"
				aria-label="scrollable auto tabs example"
			>
				{form.environments.map((environment, index) => {
					return <Tab key={index} label={environment.name} />;
				})}
				<IconButton
					key={form.environments.length + 1}
					onClick={() => {
						onChange(!form.environmentAddModalIsOpen, "environmentAddModalIsOpen");
					}}
				>
					<Add />
				</IconButton>
			</Tabs>
			{form.environments.map((environment, environmentIndex) => {
				return (
					<TabPanel key={environmentIndex} value={value} index={environmentIndex}>
						<Environment environmentId={environment._id} />
					</TabPanel>
				);
			})}
		</Container>
	);
}

ApplicationDetail.propTypes = {};
